import React from 'react'
import { Link, graphql } from 'gatsby'
import tw from 'twin.macro'
import { css } from '@emotion/react'
import { useWindowSize } from '../utils'

// Components
import Layout from '../components/Layout'
import {
  CopyStyle,
  FAQStyle,
  HeaderStyle,
  SubheadSMStyle,
  LinkButton
} from '../components/UI'
import CallToAction from '../components/CallToAction'
import { Tab } from '@headlessui/react'
import { Disclosure } from '@headlessui/react'
import { Listbox } from '@headlessui/react'
import FaqContent from '../components/FaqContent'

// Assets
import glovesCropped from '../images/gloves-cropped.webp'
import chevron from '../images/icons/chevron.svg'
import Seo from '../components/Seo'

const Faq = ({ location, data }) => {
  const pageData = data.contentfulFaqPage
  const { width } = useWindowSize()

  return (
    <Layout location={location}>
      <Seo
        title={pageData.metadataTitle || 'FAQ'}
        description={pageData.metadataDescription}
        image={
          pageData.metadataImage
            ? `https:${pageData.metadataImage.file.url}`
            : undefined
        }
      />
      {/* Hero */}
      <section tw="bg-[#E9F1F3]">
        <div tw="max-w-screen-xl mx-auto">
          <div tw="flex flex-col lg:(grid grid-cols-[1fr repeat(12, calc(1024px/12)) 1fr] relative h-[369px])">
            {/* Image */}
            <div tw="order-1 h-48 relative overflow-x-hidden lg:(order-2 col-start-8 col-span-7 h-full)">
              <img
                css={[
                  tw`w-full h-full absolute top-0 object-cover object-position[left bottom]`,
                  tw`-right-28 lg:-right-0`,
                ]}
                src={glovesCropped}
              />
            </div>
            {/* Content */}
            <div tw="order-2 mx-7 mb-16 lg:(order-1 flex flex-col justify-center mx-0 mb-0 col-start-2 col-span-5)">
              <h1 css={[HeaderStyle, tw`mb-6`]}>{pageData.heroHeadline}</h1>
              <p css={[CopyStyle, tw`max-h-full`]}>
                {pageData.heroSubhead.heroSubhead}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section tw="py-8 lg:(py-16)">
        <Tab.Group
          as="div"
          tw="grid grid-cols-4 gap-6 mx-7 lg:(max-w-screen-lg grid-cols-12 mx-auto)"
        >
          {width >= 1024 ? (
            // DESKTOP CATEGORIES
            <Tab.List tw="hidden col-span-4 lg:(block col-span-3)">
              {/* Category Group 1 */}
              {pageData.earningWithStaffyCategories && (
                <div tw="mb-7">
                  <h4 css={[SubheadSMStyle, tw`lg:(pb-2)`]}>
                    Earning with Staffy
                  </h4>
                  {pageData.earningWithStaffyCategories.map(
                    (category, index) => (
                      <Tab
                        key={`earning-with-staffy-category-${index}`}
                        as="h5"
                      >
                        {({ selected }) => (
                          <span
                            css={[
                              CopyStyle,
                              tw`block w-full text-left cursor-pointer lg:(py-2)`,
                              selected &&
                              tw`bg-[#F1FAFD] text-[#FF4D00] xl:(pl-[36px] ml-[-36px] w-[calc(100% + 36px)])`,
                            ]}
                          >
                            {category.categoryName}
                          </span>
                        )}
                      </Tab>
                    )
                  )}
                  <hr tw="border-2 border-[#F1FAFD] lg:(mt-3 ml-[-36px] w-[calc(100% + 36px)])" />
                </div>
              )}
              {/* Category Group 2 */}
              {pageData.findingTalentWithStaffyCategories && (
                <div tw="mb-7">
                  <h4 css={[SubheadSMStyle, tw`lg:(pb-2)`]}>
                    Finding Talent with Staffy
                  </h4>
                  {pageData.findingTalentWithStaffyCategories.map(
                    (category, index) => (
                      <Tab
                        key={`finding-talent-with-staffy-category-${index}`}
                        as="h5"
                      >
                        {({ selected }) => (
                          <span
                            css={[
                              CopyStyle,
                              tw`block w-full text-left cursor-pointer lg:(py-2)`,
                              selected &&
                              tw`bg-[#F1FAFD] text-[#FF4D00] xl:(pl-[36px] ml-[-36px] w-[calc(100% + 36px)])`,
                            ]}
                          >
                            {category.categoryName}
                          </span>
                        )}
                      </Tab>
                    )
                  )}
                  <hr tw="border-2 border-[#F1FAFD] lg:(mt-3 ml-[-36px] w-[calc(100% + 36px)])" />
                </div>
              )}
              {/* Category Group 3 */}
              {pageData.otherQuestionsCategories && (
                <div tw="mb-7">
                  <h4 css={[SubheadSMStyle, tw`lg:(pb-2)`]}>Other Questions</h4>
                  {pageData.otherQuestionsCategories.map((category, index) => (
                    <Tab
                      key={`other-questions-category-${index}`}
                      as="h5"
                      css={[
                        CopyStyle,
                        tw`block w-full text-left cursor-pointer lg:(py-2)`,
                      ]}
                    >
                      {category.categoryName}
                    </Tab>
                  ))}
                  <hr tw="border-2 border-[#F1FAFD] lg:(mt-3 ml-[-36px] w-[calc(100% + 36px)])" />
                </div>
              )}
            </Tab.List>
          ) : (
            // MOBILE CATEGORIES
            <Tab.List tw="block col-span-4 lg:(hidden col-span-3)">
              <Listbox as="div" tw="relative" value="Category 1">
                <Listbox.Button tw="border border-black p-7 w-full text-left flex justify-between items-center">
                  Select Category
                  <img src={chevron} />
                </Listbox.Button>
                <Listbox.Options tw="absolute border border-black bg-white w-full py-4 z-20">
                  <h4 css={[SubheadSMStyle, tw`px-7 py-3 lg:(pb-2)`]}>
                    Earning with Staffy
                  </h4>
                  <Tab
                    css={[
                      CopyStyle,
                      tw`block w-full text-left px-7 py-3 lg:(py-2)`,
                    ]}
                  >
                    Sign Up
                  </Tab>
                  <Tab
                    css={[
                      CopyStyle,
                      tw`block w-full text-left px-7 py-3 lg:(py-2)`,
                    ]}
                  >
                    Payment
                  </Tab>
                  <h4 css={[SubheadSMStyle, tw`px-7 py-3 lg:(pb-2)`]}>
                    Finding Talent with Staffy
                  </h4>
                  <Tab
                    css={[
                      CopyStyle,
                      tw`block w-full text-left px-7 py-3 lg:(py-2)`,
                    ]}
                  >
                    Sign up
                  </Tab>
                  <Tab
                    css={[
                      CopyStyle,
                      tw`block w-full text-left px-7 py-3 lg:(py-2)`,
                    ]}
                  >
                    Job Postings
                  </Tab>
                </Listbox.Options>
              </Listbox>
            </Tab.List>
          )}
          <Tab.Panels tw="col-span-4 lg:(col-start-5 col-span-8)">
            {pageData.earningWithStaffyCategories &&
              pageData.earningWithStaffyCategories.map((category, cIndex) => (
                <Tab.Panel key={`earning-cat-${cIndex}`}>
                  {category.faq.map((question, qIndex) => (
                    <Disclosure
                      as="div"
                      key={`cat-${cIndex}-question-${qIndex}`}
                      tw="border border-[#202C3A] mb-4"
                    >
                      {({ open }) => (
                        <>
                          <Disclosure.Button
                            css={[
                              FAQStyle,
                              tw`flex justify-between items-center py-7 px-8 w-full text-left`,
                            ]}
                          >
                            <h3>{question.question}</h3>
                            <img
                              src={chevron}
                              css={[
                                open &&
                                css`
                                    transform: rotate(180deg);
                                  `,
                              ]}
                            />
                          </Disclosure.Button>
                          <Disclosure.Panel tw="pb-1 px-8">
                            <FaqContent raw={question.answer.raw} />
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ))}
                </Tab.Panel>
              ))}
            {pageData.findingTalentWithStaffyCategories &&
              pageData.findingTalentWithStaffyCategories.map(
                (category, cIndex) => (
                  <Tab.Panel key={`finding-cat-${cIndex}`}>
                    {category.faq.map((question, qIndex) => (
                      <Disclosure
                        as="div"
                        key={`cat-${cIndex}-question-${qIndex}`}
                        tw="border border-[#202C3A] mb-4"
                      >
                        {({ open }) => (
                          <>
                            <Disclosure.Button
                              css={[
                                FAQStyle,
                                tw`flex justify-between items-center py-7 px-8 w-full text-left`,
                              ]}
                            >
                              <h3>{question.question}</h3>
                              <img
                                src={chevron}
                                css={[
                                  open &&
                                  css`
                                      transform: rotate(180deg);
                                    `,
                                ]}
                              />
                            </Disclosure.Button>
                            <Disclosure.Panel tw="pb-1 px-8">
                              <FaqContent raw={question.answer.raw} />
                              <div tw="mb-5">
                                <LinkButton destination="/find-talent#book-a-demo">
                                  Book a demo
                                </LinkButton>
                              </div>
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                    ))}
                  </Tab.Panel>
                )
              )}
            {pageData.otherQuestionsCategories &&
              pageData.otherQuestionsCategories.map((category, cIndex) => (
                <Tab.Panel key={`other-cat-${cIndex}`}>
                  {category.faq.map((question, qIndex) => (
                    <Disclosure
                      as="div"
                      key={`cat-${cIndex}-question-${qIndex}`}
                      tw="border border-[#202C3A] mb-4"
                    >
                      {({ open }) => (
                        <>
                          <Disclosure.Button
                            css={[
                              FAQStyle,
                              tw`flex justify-between items-center py-7 px-8 w-full text-left`,
                            ]}
                          >
                            <h3>{question.question}</h3>
                            <img
                              src={chevron}
                              css={[
                                open &&
                                css`
                                    transform: rotate(180deg);
                                  `,
                              ]}
                            />
                          </Disclosure.Button>
                          <Disclosure.Panel tw="pb-1 px-8">
                            <FaqContent raw={question.answer.raw} />
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ))}
                </Tab.Panel>
              ))}
          </Tab.Panels>
        </Tab.Group>
      </section>

      {/* CTA */}
      <CallToAction
        header={pageData.ctaHeadline}
        copy={pageData.ctaSubhead.ctaSubhead}
        ctas={[
          { copy: 'Start Earning', destination: '/start-earning' },
          { copy: 'Find Talent', destination: '/find-talent' },
        ]}
        image={pageData.ctaImage}
      />
    </Layout>
  )
}

export default Faq

export const pageQuery = graphql`
  {
    contentfulFaqPage(pageId: { eq: "FAQ Page" }) {
      heroHeadline
      heroSubhead {
        heroSubhead
      }
      earningWithStaffyCategories {
        categoryName
        faq {
          question
          answer {
            raw
          }
        }
      }
      findingTalentWithStaffyCategories {
        categoryName
        faq {
          question
          answer {
            raw
          }
        }
      }
      otherQuestionsCategories {
        categoryName
        faq {
          question
          answer {
            raw
          }
        }
      }
      ctaHeadline
      ctaSubhead {
        ctaSubhead
      }
      ctaImage {
        filename
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: NONE
          width: 1024
          quality: 80
        )
      }
      metadataTitle
      metadataDescription
      metadataImage {
        file {
          url
        }
      }
    }
  }
`
